<template>
  <div class="how-it-works-page">
    <!-- Hero Section - Using Reusable Component -->
    <Hero
      :title="$t('howItWorksPage.hero.title')"
      :subtitle="$t('howItWorksPage.hero.subtitle')"
      :carouselEnabled="true"
      :slides="heroSlides"
      :button1="{
        label: $t('howItWorksPage.hero.buttonLabel'),
        path: $t('howItWorksPage.hero.buttonPath'),
        variant: 'primary'
      }"
    />

    <!-- Certified Professionals Section -->
    <section class="professionals-intro-section">
      <div class="container">
        <div class="professionals-header">
          <h2 class="section-title">{{ $t('howItWorksPage.professionals.title') }}</h2>
          <p class="section-subtitle">{{ $t('howItWorksPage.professionals.subtitle') }}</p>
        </div>

        <div class="professionals-content">
          <div class="professionals-text">
            <div class="professional-point">
              <div class="point-icon">
                <span>✓</span>
              </div>
              <div class="point-content">
                <h4>{{ $t('howItWorksPage.professionals.point1.title') }}</h4>
                <p v-html="$t('howItWorksPage.professionals.point1.description')"></p>
              </div>
            </div>

            <div class="professional-point">
              <div class="point-icon">
                <span>✓</span>
              </div>
              <div class="point-content">
                <h4>{{ $t('howItWorksPage.professionals.point2.title') }}</h4>
                <p>{{ $t('howItWorksPage.professionals.point2.description') }}</p>
              </div>
            </div>

            <div class="professional-point">
              <div class="point-icon">
                <span>✓</span>
              </div>
              <div class="point-content">
                <h4>{{ $t('howItWorksPage.professionals.point3.title') }}</h4>
                <p>{{ $t('howItWorksPage.professionals.point3.description') }}</p>
              </div>
            </div>
          </div>

          <div class="professionals-cta">
            <div
              class="cta-card"
              :style="{
                backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.5) 100%), url('${$t('howItWorksPage.professionals.cta.backgroundImage')}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }"
            >
              <h3>{{ $t('howItWorksPage.professionals.cta.title') }}</h3>
              <p>{{ $t('howItWorksPage.professionals.cta.description') }}</p>
              <router-link :to="$t('howItWorksPage.professionals.cta.buttonPath')" class="btn btn-primary btn-large">
                {{ $t('howItWorksPage.professionals.cta.buttonLabel') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Four Pillars Section -->
    <section class="four-pillars-section">
      <div class="container">
        <h2 class="pillars-headline">{{ $t('howItWorksPage.pillars.title') }}</h2>
        <p class="pillars-subheading">{{ $t('howItWorksPage.pillars.subtitle') }}</p>

        <div class="pillars-grid">
          <div v-for="(pillar, index) in pillars" :key="index" class="pillar-item">
            <div class="pillar-icon">
              <component :is="pillar.icon" />
            </div>
            <h3>{{ $t(`howItWorksPage.pillars.items.${index}.title`) }}</h3>
            <p>{{ $t(`howItWorksPage.pillars.items.${index}.description`) }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Movement Section: Image LEFT, Content RIGHT -->
    <section class="category-section movement-section">
      <div class="container">
        <div class="section-content movement-layout">
          <img
            :src="$t('howItWorksPage.movement.image')"
            alt="Movement and fitness"
            class="section-image section-photo"
          />
          <div class="section-text">
            <h3>{{ $t('howItWorksPage.movement.title') }}</h3>
            <div class="columns-grid">
              <div class="column">
                <h4>{{ $t('howItWorksPage.movement.training.title') }}</h4>
                <ul>
                  <li v-for="(item, index) in movementTraining" :key="index">
                    {{ item }}
                  </li>
                </ul>
              </div>
              <div class="column">
                <h4>{{ $t('howItWorksPage.movement.recovery.title') }}</h4>
                <ul>
                  <li v-for="(item, index) in movementRecovery" :key="index">
                    {{ item }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Meals Section: Content LEFT, Image RIGHT -->
    <section class="category-section meals-section">
      <div class="container">
        <div class="section-content meals-layout">
          <div class="section-text">
            <h3>{{ $t('howItWorksPage.meals.title') }}</h3>
            <div class="columns-grid">
              <div class="column">
                <ul>
                  <li v-for="(item, index) in mealsFirst" :key="index">
                    {{ item }}
                  </li>
                </ul>
              </div>
              <div class="column">
                <ul>
                  <li v-for="(item, index) in mealsSecond" :key="index">
                    {{ item }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <img
            :src="$t('howItWorksPage.meals.image')"
            alt="Healthy meals and nutrition"
            class="section-image section-photo"
          />
        </div>
      </div>
    </section>

    <!-- Mind Section: Image LEFT, Content RIGHT -->
    <section class="category-section mind-section">
      <div class="container">
        <div class="section-content mind-layout">
          <img
            :src="$t('howItWorksPage.mind.image')"
            alt="Mental health and wellness"
            class="section-image section-photo"
          />
          <div class="section-text">
            <h3>{{ $t('howItWorksPage.mind.title') }}</h3>
            <div class="columns-grid">
              <div class="column">
                <ul>
                  <li v-for="(item, index) in mindFirst" :key="index">
                    {{ item }}
                  </li>
                </ul>
              </div>
              <div class="column">
                <ul>
                  <li v-for="(item, index) in mindSecond" :key="index">
                    {{ item }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Money Section: Content LEFT, Image RIGHT -->
    <section class="category-section money-section">
      <div class="container">
        <div class="section-content money-layout">
          <div class="section-text">
            <h3>{{ $t('howItWorksPage.money.title') }}</h3>
            <div class="columns-grid">
              <div class="column">
                <ul>
                  <li v-for="(item, index) in moneyFirst" :key="index">
                    {{ item }}
                  </li>
                </ul>
              </div>
              <div class="column">
                <ul>
                  <li v-for="(item, index) in moneySecond" :key="index">
                    {{ item }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <img
            :src="$t('howItWorksPage.money.image')"
            alt="Financial planning and wealth"
            class="section-image section-photo"
          />
        </div>
      </div>
    </section>

    <!-- Final CTA Section -->
    <div class="final-cta-wrapper">
      <FinalCTA />
    </div>
  </div>
</template>

<script>
import Hero from '../sections/Hero.vue';
import FinalCTA from '../sections/FinalCTA.vue';
import CameraVideoIcon from './icons/CameraVideoIcon.vue';
import ClockIcon from './icons/ClockIcon.vue';
import ThumbsUpIcon from './icons/ThumbsUpIcon.vue';

export default {
  name: 'HowItWorks',
  components: {
    Hero,
    FinalCTA,
    CameraVideoIcon,
    ClockIcon,
    ThumbsUpIcon
  },
  data() {
    return {
      heroSlides: [this.$t('howItWorksPage.hero.slideImage')],
      pillars: [
        {
          icon: 'CameraVideoIcon'
        },
        {
          icon: 'ClockIcon'
        },
        {
          icon: 'ThumbsUpIcon'
        }
      ],
      movementTraining: [
        'Alexander Technique',
        'Beach Body Coach',
        'BOSU Balance Trainers',
        'Certified Strength and Conditioning Specialist',
        'Chek Exercise Coach',
        'CrossFit Trainer',
        'Cycling Coach',
        'Feldenkrais Practitioner',
        'GYROKINESIS Method',
        'High-Intensity-Interval Training',
        'IBBFA Bare Method Instructor',
        'International Association of Fitness Science',
        'Personal Trainer',
        'Personal Yoga Instructor',
        'Pilates Instructor',
        'Running Coach',
        'Spartan SGX Trainer',
        'Triathlon / Swimming Coach',
        'TRX Instructor',
        'World OCR Coach',
        'Yoga Instructor - all styles',
        'and many more!'
      ],
      movementRecovery: [
        'Acupressure Practitioner',
        'Acupuncture Practitioner',
        'Ayurveda Practitioner',
        'Chek Facilitator',
        'Chiropractor',
        'Contrast Therapy',
        'Cryotherapy',
        'Health & Fitness Coach',
        'Massage Therapist',
        'Aromatherapy',
        'Deep Tissue massage',
        'Hot Stone massage',
        'Prenatal massage',
        'Reflexology',
        'Reiki Therapist',
        'Massage Therapy',
        'Physical Therapy',
        'Yin Yoga',
        'Yoga Therapist',
        'and many more!'
      ],
      mealsFirst: [
        'Ayurveda',
        'Diet Coach',
        'Dietitian',
        'Fasting Coach',
        'Keto Coach',
        'Naturopathic Doctor',
        'Nutritional Coach',
        'Nutritional Studies Coach'
      ],
      mealsSecond: [
        'Nutritionist',
        'Paleo Coach',
        'Personal Chef',
        'Plant-Based Coach',
        'Raw Food Transition Coach',
        'Vegan Lifestyle Coach',
        'Weight Loss Coach',
        'and many more!'
      ],
      mindFirst: [
        'Addiction Counselor',
        'Biofeedback Therapist',
        'Breathwork Facilitator',
        'Certified Life Coach',
        'Cancer Recovery Coach',
        'Chronic Illness Recovery Coach',
        'Cognitive Behavioral Therapy',
        'Eco Therapist',
        'EFT/TFT Tapping Practitioners',
        'EMDR-Eye Movement Desensitization/Reprocessing Therapy',
        'Health & Wellness Coach'
      ],
      mindSecond: [
        'Health Recovery Coach',
        'Holistic Health Coach',
        'Hypnotherapist',
        'Meditation Teacher',
        'Mental Health Practitioner',
        'Mentors',
        'Motivational Coach',
        'Psychologist',
        'Sleep Therapist',
        'Somatic Therapy',
        'Thought Coach',
        'Tony Robbins Results Coach',
        'and many more!'
      ],
      moneyFirst: [
        'Accredited Financial Counselors',
        'AFCPE Certified Professionals',
        'Cash Flow Coaching',
        'Certified Financial Educators (CFEd.)',
        'Certified Financial Coaches',
        'Certified Personal Financial Consultants (NFEC)',
        'Cryptocurrency and Blockchain teachers',
        'Financial Planners'
      ],
      moneySecond: [
        'Financial Fitness Coach (FFC)',
        'Hey Money Experts',
        'Life Coaches',
        'Results Coaching',
        'Startup Advisors',
        'Tony Robbins Results Coaches',
        'Wealth Coaching',
        'and much more!'
      ]
    };
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/_variables.scss';

.how-it-works-page {
  width: 100%;
  background: $bg-white;
  color: $text-dark;
  font-family: $font-family-body;
  line-height: 1.6;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
}

.btn {
  display: inline-block;
  padding: 12px 32px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;

  &.btn-primary {
    background: $brand-color;
    color: white;

    &:hover {
      background: $brand-dark;
      transform: translateY(-2px);
      box-shadow: 0 12px 24px rgba($brand-color, 0.2);
    }
  }

  &.btn-secondary {
    background: transparent;
    color: white;
    border: 2px solid white;

    &:hover {
      background: $brand-color;
    }
  }
}

/* Professionals Intro Section */
.professionals-intro-section {
  padding: 5rem 2rem;
  background: linear-gradient(135deg, $bg-light 0%, rgba($brand-color, 0.05) 100%);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 500px;
    height: 500px;
    background: rgba($brand-color, 0.08);
    border-radius: 50%;
    z-index: 0;
  }
}

.professionals-header {
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
  z-index: 1;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-align: center;
  color: $text-dark;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
}

.section-subtitle {
  font-size: 1.3rem;
  color: $brand-color;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.professionals-content {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 1;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
}

.professionals-text {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.professional-point {
  display: flex;
  gap: 1.5rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  border-left: 4px solid $brand-color;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(8px);
    box-shadow: 0 8px 24px rgba($brand-color, 0.15);
  }

  h4 {
    font-size: 1.1rem;
    font-weight: 700;
    color: $text-dark;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.95rem;
    color: $text-light;
    line-height: 1.6;
    margin: 0;

    strong {
      color: $brand-color;
      font-weight: 600;
    }
  }
}

.point-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  background: $brand-color;
  border-radius: 50%;
  color: white;
  font-weight: 700;
  font-size: 1.2rem;
}

.point-content {
  flex: 1;
}

.professionals-cta {
  display: flex;
  align-items: center;
}

.cta-card {
  color: white;
  padding: 2.5rem;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.35);
  }

  h3 {
    font-size: 1.8rem;
    margin-bottom: 0.75rem;
    font-weight: 700;
  }

  p {
    font-size: 0.95rem;
    margin-bottom: 1.5rem;
    opacity: 0.95;
    line-height: 1.6;
  }

  @media (max-width: 968px) {
    padding: 2rem;

    h3 {
      font-size: 1.5rem;
    }
  }
}

.btn-large {
  padding: 14px 40px;
  font-size: 1.05rem;
  width: 100%;
  background: white;
  color: $brand-color;
  font-weight: 700;

  &:hover {
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  }
}

/* Four Pillars Section */
.four-pillars-section {
  padding: 5rem 2rem;
  background: white;

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
}

.pillars-headline {
  font-size: 2.8rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
}

.pillars-subheading {
  font-size: 1.2rem;
  color: $text-light;
  text-align: center;
  margin-bottom: 3rem;
}

.pillars-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
}

.pillar-item {
  text-align: center;
  padding: 2rem;
  background: $bg-light;
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: white;
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
    transform: translateY(-4px);
  }

  h3 {
    font-size: 1.4rem;
    margin: 1.5rem 0 0.75rem;
  }

  p {
    color: $text-light;
    font-size: 0.95rem;
    line-height: 1.6;
  }
}

.pillar-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $brand-color;
  border-radius: 50%;
  color: white;
  font-size: 2rem;

  svg {
    width: 100%;
    height: 100%;
    padding: 10px;
  }
}

/* Category Sections */
.category-section {
  padding: 5rem 2rem;

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }

  h3 {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 2rem;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }
}

.movement-section {
  background: white;
}

.meals-section {
  background: $bg-light;
}

.mind-section {
  background: white;
}

.money-section {
  background: $bg-light;
}

.section-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: flex-start;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
}

.section-content.movement-layout {
  grid-template-columns: 1fr 1fr;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
}

.section-content.meals-layout {
  grid-template-columns: 1fr 1fr;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
}

.meals-layout {
  @media (max-width: 968px) {
    display: flex;
    flex-direction: column-reverse;
  }
}

.section-content.mind-layout {
  grid-template-columns: 1fr 1fr;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
}

.section-content.money-layout {
  grid-template-columns: 1fr 1fr;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
}

.money-layout {
  @media (max-width: 968px) {
    display: flex;
    flex-direction: column-reverse;
  }
}

.section-image {
  width: 100%;
  height: 100%;
  min-height: 400px;
  background: linear-gradient(135deg, rgba($brand-color, 0.1), rgba($brand-dark, 0.1));
  border-radius: 12px;

  @media (max-width: 968px) {
    min-height: 300px;
  }
}

.section-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
  display: block;
}

.section-text {
  h4 {
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: $brand-color;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      padding: 0.5rem 0;
      padding-left: 1.5rem;
      position: relative;
      color: $text-light;

      &:before {
        content: '→';
        position: absolute;
        left: 0;
        color: $brand-color;
        font-weight: 700;
      }
    }
  }
}

.columns-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}

.column {
  @media (max-width: 768px) {
    &:not(:first-child) {
      display: none;
    }
  }
}

.final-cta-wrapper {
  margin-bottom: -5rem;
}
</style>
